/**
 * کیبوردِ گوشی روی سایت (WHOSUP-223) — قرینه‌ی `apps/mobile/src/ui/keyboard.tsx`.
 *
 * ═══ چرا CSS به‌تنهایی کافی نیست ═══
 *
 * وقتی کیبوردِ گوشی بالا می‌آید، مرورگر به‌طورِ پیش‌فرض فقط **ویوپورتِ
 * دیداری** را کوچک می‌کند و ویوپورتِ چیدمان دست‌نخورده می‌ماند. یعنی هر چیزی
 * که `position: fixed` است — در این سایت: پس‌زمینه‌ی مودال‌ها — همان‌قدر بلند
 * می‌ماند و وسط‌چین بودنش را نسبت به صفحه‌ی **کامل** حساب می‌کند، نه نسبت به
 * آن‌چه دیده می‌شود. نتیجه‌اش دقیقاً همان شکایتِ تیکت است: فرمِ گزارش یا
 * ساختِ گروه باز می‌شود، کیبورد می‌آید، و کادرِ متن می‌رود زیرش.
 *
 * دو تکه جوابش می‌دهند و هر دو لازم‌اند:
 *
 *   • `interactive-widget=resizes-content` در `index.html` — کروم روی اندروید
 *     را وامی‌دارد خودِ ویوپورتِ چیدمان را کوچک کند. آن‌وقت `fixed` و `dvh` و
 *     خودِ «فیلد را بیاور جلوی چشم»ِ مرورگر همه با هم درست می‌شوند، بدونِ
 *     یک خط جاوااسکریپت.
 *   • همین فایل، برای سافاریِ iOS که آن تنظیم را نمی‌شناسد: اندازه‌ی کیبورد
 *     را از `visualViewport` می‌گیرد و در `--keyboard-inset` می‌نویسد تا CSS
 *     بتواند کنارش بکشد.
 *
 * و چون در حالتِ اول ویوپورت خودش کوچک شده، عددِ این‌جا صفر در می‌آید و
 * کیبورد دو بار حساب نمی‌شود — همان کاری که `keyboardOverlap` تضمین می‌کند.
 */
import { keyboardOverlap, revealShift } from '@/core/lib/keyboardAvoid';

/** نفسی که بینِ فیلد و لبه‌ی کیبورد می‌ماند. */
const BREATH = 12;

/**
 * کمتر از این یعنی کیبورد نیست.
 *
 * نوارِ آدرسِ مرورگر که با اسکرول جمع و باز می‌شود هم ویوپورتِ دیداری را چند
 * ده پیکسل تکان می‌دهد. بدونِ این آستانه، هر اسکرولِ ساده مودال‌ها را کمی
 * بالا و پایین می‌پراند — همان «پرشِ بی‌دلیل»ی که تیکت صریحاً نمی‌خواهد.
 * هیچ کیبوردِ نرم‌افزاریِ واقعی هم از این کوتاه‌تر نیست.
 */
const MIN_KEYBOARD = 120;

const VAR = '--keyboard-inset';

/** فیلد است یا نه — `select` هم هست چون روی iOS چرخ‌فلکِ خودش را بالا می‌آورد. */
function isField(el: Element | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  return el instanceof HTMLInputElement
    || el instanceof HTMLTextAreaElement
    || el instanceof HTMLSelectElement
    || el.isContentEditable;
}

/**
 * فیلدی که الان فوکوس دارد را از زیرِ کیبورد بیرون می‌کشد.
 *
 * اول `scrollIntoView` با `block: 'nearest'`: همان کاری که مرورگر خودش
 * می‌کند، و ظرفِ اسکرول‌شونده‌ی نزدیک (بدنه‌ی مودال) را هم شامل می‌شود. ولی
 * «نزدیک» را نسبت به ویوپورتِ چیدمان حساب می‌کند، که روی iOS کیبورد را
 * نمی‌بیند — پس بعدش خودمان اندازه می‌گیریم و اگر هنوز زیرش بود، صفحه را
 * همان‌قدر می‌لغزانیم و نه بیشتر.
 */
function reveal(inset: number): void {
  const el = document.activeElement;
  if (!isField(el)) return;
  el.scrollIntoView({ block: 'nearest' });

  const box = el.getBoundingClientRect();
  if (!box.height) return;
  const shift = revealShift(
    { top: box.top, bottom: box.bottom },
    { top: 0, bottom: window.innerHeight, keyboard: inset, margin: BREATH },
  );
  if (Math.abs(shift) < 1) return;
  window.scrollBy({ top: shift, behavior: 'smooth' });
}

/**
 * `--keyboard-inset` را زنده نگه می‌دارد و فیلدِ فوکوس‌دار را زیرِ کیبورد جا
 * نمی‌گذارد. یک بار در `boot.ts` صدا زده می‌شود.
 */
export function installKeyboardInset(): void {
  const view = window.visualViewport;
  // مرورگرِ بدونِ `visualViewport` (خیلی قدیمی) یا محیطِ تست: متغیر روی صفر
  // می‌ماند و همه‌چیز دقیقاً مثلِ قبل رفتار می‌کند.
  if (!view) return;

  let inset = 0;

  /**
   * قدِ کیبورد، یا صفر.
   *
   * شرطِ «فیلدی فوکوس دارد» تزئینی نیست و همان چیزی‌ست که این را از یک حدسِ
   * شکننده به یک قاعده تبدیل می‌کند: ویوپورتِ دیداری با جمع شدنِ نوارهای
   * سافاری هم کوچک می‌شود، و آن تغییر روی بعضی آیفون‌ها از آستانه‌ی زیر هم
   * رد می‌شد. کیبورد بدونِ فیلدِ فوکوس‌دار اصلاً وجود ندارد، پس ساده‌ترین
   * راهِ تشخیص همین است.
   */
  const measure = (): number => {
    if (!isField(document.activeElement)) return 0;
    const raw = keyboardOverlap(window.innerHeight, view.height + view.offsetTop);
    return raw >= MIN_KEYBOARD ? Math.round(raw) : 0;
  };

  const update = (): void => {
    const next = measure();
    if (next === inset) return;
    inset = next;
    document.documentElement.style.setProperty(VAR, `${inset}px`);
    // اول متغیر، بعد اندازه‌گیری: چیدمانِ تازه (مودالی که حالا کوتاه‌تر است)
    // باید پیش از حساب کردنِ جای فیلد اعمال شده باشد.
    if (inset > 0) requestAnimationFrame(() => reveal(inset));
  };

  view.addEventListener('resize', update);
  // نوارِ آدرس که جمع می‌شود `offsetTop` عوض می‌شود بی‌آنکه `resize` بیاید.
  view.addEventListener('scroll', update);

  /**
   * فوکوس هم بخشی از جواب است (بالا را ببین)، و رفتن از فیلدی به فیلدِ دیگر
   * هیچ `resize`ی نمی‌سازد. تأخیرِ کوچک چون در همان لحظه‌ی فوکوس، کیبورد
   * هنوز در راه است و چیدمان هنوز جابه‌جا می‌شود.
   */
  const onFocus = (): void => {
    update();
    setTimeout(() => { update(); if (inset > 0) reveal(inset); }, 150);
  };
  window.addEventListener('focusin', onFocus);
  /**
   * فیلد که ول شد، کیبورد هم می‌رود و چیدمان باید برگردد سرِ جایش.
   *
   * ⚠ با یک تیک تأخیر، و این مهم است: رفتن از فیلدی به فیلدِ دیگر یعنی
   * `focusout` و بعد `focusin`، و در فاصله‌شان `activeElement` خودِ `body`
   * است. بدونِ تأخیر، هر بار که کاربر از کادری به کادرِ بعدی می‌رفت مودال
   * یک بار می‌افتاد پایین و دوباره بالا می‌آمد.
   */
  window.addEventListener('focusout', () => { setTimeout(update, 0); });

  update();
}
